import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnOrderState,
  type ColumnSizingState,
  type Row,
  type SortingState,
  type Table,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from 'react';

import type {
  GridColumnDef,
  SortingRule,
  TableAppStore,
  TablePaginatedV8CoreProps,
} from '../table-types';
import { TableDataState } from '../table-types';
import { resolveColumnUpdater, useColumnPersistence } from './use-column-persistence';
import type { useTableEditing } from './use-table-editing';

const EMPTY_SORTING: SortingState = [];

function cloneRows<TData>(rows: readonly TData[]): TData[] {
  return rows ? JSON.parse(JSON.stringify(rows)) : [];
}

/**
 * Το API στέλνει orderByField (π.χ. region.name) ενώ το TanStack χρησιμοποιεί column id.
 * Χωρίς αντιστοίχιση το βέλος ASC/DESC χάνεται και το toggle μένει πάντα ASC.
 */
function mapSortingToColumnIds<TData extends object>(
  sorting: SortingState,
  columns: ReadonlyArray<GridColumnDef<TData>>,
): SortingState {
  if (!sorting?.length || !columns?.length) return sorting ?? EMPTY_SORTING;
  return sorting.map((item) => {
    const col = columns.find(
      (c) =>
        c.id === item.id ||
        c.orderByField === item.id ||
        (typeof c.accessorKey === 'string' && c.accessorKey === item.id),
    );
    return col?.id && col.id !== item.id ? { ...item, id: String(col.id) } : item;
  });
}

const browseOnlyTableMeta = {
  dataState: TableDataState.browse,
  editedRows: {} as Record<string, boolean>,
  setEditedRows: (() => undefined) as Dispatch<SetStateAction<Record<string, boolean>>>,
  updateData: () => undefined,
  updateRow: async () => false,
  addNewRow: async () => undefined,
  deleteRow: async () => undefined,
  editRow: async () => false,
  saveRow: async () => false,
  cancelRow: () => undefined,
  revertData: () => undefined,
  getRow: () => undefined,
  setDataValue: () => undefined,
  onBeforeEditRow: null,
  onBeforeNewRow: null,
};

export type PaginatedTableV8State<TData extends object> = {
  table: Table<TData>;
  saveColumns: ReturnType<typeof useColumnPersistence>['saveColumns'];
  handlePageChange: (pageNo: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  scrollContainerStyle: CSSProperties;
  displayData: TData[];
};

type EditingResult<TData extends object> = ReturnType<typeof useTableEditing<TData>>;

/**
 * Shared TanStack table setup for {@link TablePaginatedV8Core} and {@link TableEditableV8}.
 */
export function usePaginatedTableV8<TData extends object>(
  props: TablePaginatedV8CoreProps<TData>,
  editing: EditingResult<TData> | null,
): PaginatedTableV8State<TData> {
  const {
    id,
    appStore,
    columns: defaultColumns,
    data: defaultData,
    dataCount,
    initPageIndex = 0,
    fixedPageSize,
    onPageChange = null,
    initSorting = EMPTY_SORTING,
    onSortByChange = null,
    onSelectedRowsChange = null,
    onRowSelectionChange = null,
    getRowId = null,
    initRowSelection = {},
    enableManualSorting = true,
    showGridFooter = true,
    style = {},
  } = props;

  const { saveColumns, restoreColumnState } = useColumnPersistence(id);
  const [columns] = useState(() => [...defaultColumns]);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columns.map((column) => column.id as string),
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const resolvedInitSorting = useMemo(
    () => mapSortingToColumnIds(initSorting, columns),
    [initSorting, columns],
  );
  const [sorting, setSorting] = useState<SortingState>(resolvedInitSorting);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>(initRowSelection);

  const readOnlyData = useMemo(() => cloneRows(defaultData), [defaultData]);
  const displayData = editing?.data ?? readOnlyData;

  const effectivePageSize =
    fixedPageSize ?? (showGridFooter ? appStore.pageSize : displayData.length);
  const controlledPageCount = ~~((dataCount - 1) / effectivePageSize) + 1;

  useEffect(() => {
    setSorting((prevSorting) => {
      const prevSignature = JSON.stringify(prevSorting ?? []);
      const nextSignature = JSON.stringify(resolvedInitSorting ?? []);
      return prevSignature === nextSignature ? prevSorting : resolvedInitSorting;
    });
  }, [resolvedInitSorting]);

  // Καλούμε μόνο όταν αλλάζει το rowSelection — όχι όταν αλλάζει το identity του callback
  // (αλλιώς κάθε re-render του parent, π.χ. αλλαγή σελίδας στο modal, ξαναγεμίζει την επιλογή)
  const onRowSelectionChangeRef = useRef(onRowSelectionChange);
  onRowSelectionChangeRef.current = onRowSelectionChange;

  useEffect(() => {
    if (onRowSelectionChangeRef.current) {
      onRowSelectionChangeRef.current(rowSelection);
    }
  }, [rowSelection]);

  const table = useReactTable({
    columns,
    data: displayData,
    initialState: {
      pagination: {
        pageIndex: initPageIndex,
        pageSize: effectivePageSize,
      },
    },
    manualPagination: true,
    manualSorting: enableManualSorting,
    enableMultiSort: false,
    enableSortingRemoval: false,
    pageCount: controlledPageCount,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      rowSelection,
      columnOrder,
      sorting,
      columnSizing,
      columnVisibility,
    },
    onColumnOrderChange: (state) => {
      setColumnOrder((current) => {
        const next = resolveColumnUpdater(state, current);
        saveColumns({ columnOrder: next });
        return next;
      });
    },
    onColumnSizingChange: (state) => {
      setColumnSizing((current) => {
        const nextPartial = resolveColumnUpdater(state, current);
        const next = { ...current, ...nextPartial };
        saveColumns({ columnSizing: next });
        return next;
      });
    },
    onColumnVisibilityChange: (state) => {
      setColumnVisibility((current) => {
        const nextPartial = resolveColumnUpdater(state, current);
        const next = { ...current, ...nextPartial };
        saveColumns({ columnVisibility: next });
        return next;
      });
    },
    onRowSelectionChange: setRowSelection,
    getRowId: getRowId ?? undefined,
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = resolveColumnUpdater(updater, prev);
        if (onSortByChange && next.length > 0) {
          const columnId = next[0].id;
          const sortOrder = next[0].desc ? 'DESC' : 'ASC';
          const cols = columns.filter((r) => r.id === columnId);
          let col: GridColumnDef<TData> | undefined;
          let order = 'id';
          if (cols.length === 1) {
            col = cols[0];
            order = `${col.orderByField ?? 'id'} ${sortOrder}`;
          }
          onSortByChange(next as SortingRule<TData>[], col, order);
        }
        return next;
      });
    },
    columnResizeMode: 'onChange',
    meta: editing?.tableMeta ?? browseOnlyTableMeta,
  });

  useEffect(() => {
    editing?.bindTable(table);
  }, [editing, table]);

  useEffect(() => {
    table.setPageIndex(initPageIndex);
  }, [initPageIndex, table]);

  useEffect(() => {
    restoreColumnState(table as Table<unknown>);
  }, [displayData, restoreColumnState, table]);

  useEffect(() => {
    if (!onSelectedRowsChange) return;
    const flatRows = table.getRowModel().flatRows;
    const selectedFlatRows = flatRows.filter((_value, index) => rowSelection[index]);
    onSelectedRowsChange(selectedFlatRows as Row<TData>[]);
  }, [onSelectedRowsChange, rowSelection, table]);

  const handlePageChange = useCallback(
    (pageNo: number) => {
      if (onPageChange) {
        onPageChange(pageNo, table.getState().pagination.pageSize);
      }
      table.setPageIndex(pageNo);
    },
    [onPageChange, table],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      if (fixedPageSize) return;
      appStore.setPageSize(pageSize);
      table.setPageSize(pageSize);
      table.setPageIndex(0);
      if (onPageChange) onPageChange(0, pageSize);
    },
    [appStore, fixedPageSize, onPageChange, table],
  );

  const {
    overflow: styleOverflow,
    overflowX: styleOverflowX,
    overflowY: styleOverflowY,
    minWidth: styleMinWidth,
    ...restStyle
  } = style ?? {};

  const scrollContainerStyle: CSSProperties = {
    ...restStyle,
    minWidth: styleMinWidth ?? '1024px',
    maxWidth: '100%',
    overflowX: styleOverflowX ?? 'auto',
    overflowY: (styleOverflowY ?? styleOverflow) as CSSProperties['overflowY'],
  };

  return {
    table,
    saveColumns,
    handlePageChange,
    handlePageSizeChange,
    scrollContainerStyle,
    displayData,
  };
}

export type { TableAppStore };
